import {  Injectable, Injector, NgZone, inject, runInInjectionContext } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  from,
  map,
  of,
  tap,
  throwError,
  filter,
  distinctUntilChanged
} from 'rxjs';
import * as Oidc from 'oidc-client';
import {  User, UserManager } from 'oidc-client';
import { AppSettingsService, UtilsService, environment } from '@dabnet-kamu/libs/ui';
// eski hâli (06.10.2026): çıkışta inject edilince eski SSE bağlantısını AÇIYORDU (normalde hiç başlamıyordu)
// import { NotificationService } from 'libs/ui/src/lib/shared/services/notification.service';
import { RealtimeService } from 'libs/ui/src/lib/shared/services/realtime.service';
import {take} from "rxjs/operators";
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Router } from '@angular/router';


@Injectable({ providedIn: 'root' })
export class AuthService {

  public userManager = new UserManager(environment.identityConfig);
  public userSubject = new BehaviorSubject<User | null>(null);

  readonly user$ = this.userSubject.asObservable();

  private utilService = inject(UtilsService);
  private injector = inject(Injector);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  // 🔹 Silent renew state
  private silentRenewPromise: Promise<User | null> | null = null;
  private silentRenewFailed = false;

  // 🔹 Native (Capacitor/APK) OIDC dönüşü — dabid.dabto.com'daki MobilClient'a
  // bu iki adres redirect URI olarak eklenmeli (AngularClient'a değil — mobil
  // production ayarları ayrı bir client_id kullanıyor). Web akışını hiç etkilemez.
  private readonly NATIVE_CALLBACK_URL = 'com.dabto.mobilekamu://callback';
  private readonly NATIVE_LOGOUT_URL = 'com.dabto.mobilekamu://logout';

  // 🔹 Android singleTask launch mode, son alınan appUrlOpen intent'ini task
  // seviyesinde saklıyor ve uygulama tamamen kapatılıp yeniden açıldığında bile
  // aynı (artık tek kullanımlık kodu tükenmiş) callback URL'sini tekrar iletiyor.
  // Aynı URL'yi ikinci kez işlemeyi engelliyoruz.
  private readonly LAST_HANDLED_NATIVE_AUTH_URL_KEY = 'lastHandledNativeAuthUrl';

  constructor() {
    const appSettingsService = inject(AppSettingsService);

    appSettingsService.settings$
      .pipe(filter(Boolean))
      .subscribe(() => this.initializeUser());

    if (Capacitor.isNativePlatform()) {
      this.setupNativeAuthListener();
    }
  }

  // 🔹 Native: sistem tarayıcısında (Custom Tabs) açılan login/logout'un
  // özel URL şeması (com.dabto.mobilekamu://...) ile geri dönüşünü yakalar.
  private setupNativeAuthListener(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      // 🆕 Bu olay Capacitor köprüsünden (native taraf) geliyor, Angular'ın
      // "zone"unun DIŞINDA çalışıyor olabilir — yani router/state değişiklikleri
      // gerçekleşse bile ekran yeniden çizilmeyebilir. Hepsini NgZone.run içine alıyoruz.
      this.ngZone.run(() => {
        const url = event.url;

        if (localStorage.getItem(this.LAST_HANDLED_NATIVE_AUTH_URL_KEY) === url) {
          return;
        }

        if (url.startsWith(this.NATIVE_CALLBACK_URL) || url.startsWith(this.NATIVE_LOGOUT_URL)) {
          localStorage.setItem(this.LAST_HANDLED_NATIVE_AUTH_URL_KEY, url);
        }

        if (url.startsWith(this.NATIVE_CALLBACK_URL)) {
          Browser.close().catch(() => {});
          this.userManager
            .signinRedirectCallback(url)
            .then(user => {
              this.ngZone.run(() => {
                this.userSubject.next(user);
                this.router.navigateByUrl('/main');
              });
            })
            .catch(err => {
              this.utilService.showError('Giriş tamamlanamadı: ' + (err?.message ?? err));
            });
        } else if (url.startsWith(this.NATIVE_LOGOUT_URL)) {
          Browser.close().catch(() => {});
          this.userManager
            .signoutRedirectCallback(url)
            .catch(() => {})
            .finally(() => {
              this.ngZone.run(() => {
                this.userSubject.next(null);
                localStorage.removeItem('kullanici');
                localStorage.removeItem(
                  `oidc.user:${environment.identityConfig.authority}:${environment.identityConfig.client_id}`
                );
                this.router.navigateByUrl('/');
              });
            });
        }
      });
    });
  }

  // 🔹 User load
  initializeUser(): void {
    from(this.userManager.getUser())
      .pipe(
        tap(user => this.userSubject.next(user)),
        catchError(err => {
          this.userSubject.next(null);
          this.utilService.showError(err);
          return of(null);
        })
      )
      .subscribe();
  }

  // 🔹 SAFE sync access (sadece gerekiyorsa)
  get loggedUser(): User | null {
    return this.userSubject.value;
  }

  /**
   * 🔹 Access token expiration kontrolü
   * - OIDC 'expires_at' alanını kullanır, yoksa 'user.expired'e düşer
   * - skewSeconds ile birkaç saniyelik tolerans verilebilir
   */
  isAccessTokenExpired(user: User | null, skewSeconds: number = 0): boolean {
    if (!user) return true;

    const expiresAt = (user as any).expires_at as number | undefined;
    if (typeof expiresAt === 'number') {
      const now = Math.floor(Date.now() / 1000);
      return now >= (expiresAt - skewSeconds);
    }

    // expires_at yoksa fallback olarak oidc-client'ın hesapladığı değeri kullan
    return !!user.expired;
  }

  // 🔹 Token READY garantili stream
  accessToken$(): Observable<string> {
    return this.user$.pipe(
      filter((u): u is User => !!u && !u.expired),
      map(u => u.access_token),
      distinctUntilChanged()
    );
  }

  // 🔹 Login state
  isLoggedIn$(): Observable<boolean> {
    return this.user$.pipe(
      map(u => !!u && !u.expired),
      distinctUntilChanged()
    );
  }

  // 🔹 Redirect login
  async startAuthentication(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      // WebView içinde değil, sistem tarayıcısında (Custom Tabs) aç —
      // dönüş setupNativeAuthListener()'daki appUrlOpen ile yakalanır.
      const req = await this.userManager.createSigninRequest({
        redirect_uri: this.NATIVE_CALLBACK_URL,
      });
      await Browser.open({ url: req.url });
      return;
    }

    return this.userManager.signinRedirect();
  }

  completeAuthentication(): Observable<User> {
  
    return from(this.userManager.signinRedirectCallback()).pipe(
      tap(user => this.userSubject.next(user))
    );
  }

  /**
   * 🔹 Programatik silent renew (signinSilent) için merkezi helper
   * - Eğer user yoksa veya expired ise 1 kez silent renew dener
   * - Silent renew hata verirse (timeout dahil) bir daha denemez, hata fırlatır
   * - Çağıran yer hata aldığında kendi içinde forceLogout kararı verir
   */
  private runSilentRenew(): Promise<User | null> {
    if (this.silentRenewPromise) {
      return this.silentRenewPromise;
    }

    this.silentRenewPromise = this.userManager
      .signinSilent()
      .then(user => {
        this.userSubject.next(user ?? null);
        return user ?? null;
      })
      .catch(err => {
        // Herhangi bir silent renew hatasında bir daha otomatik deneme
        // yapılmaması için flag işaretlenir.
        this.silentRenewFailed = true;
        throw err;
      })
      .finally(() => {
        this.silentRenewPromise = null;
      });

    return this.silentRenewPromise;
  }

  /**
   * 🔹 Geçerli (expired=false) user garantisi
   * - Zaten valid user varsa direkt döner
   * - Değilse 1 kez silent renew dener
   * - Başarısız olursa (ör. silentRequestTimeout) hata fırlatır
   */
  async ensureValidUser(): Promise<User> {
    let user = this.loggedUser;

    if (user && !user.expired) {
      return user;
    }

    if (this.silentRenewFailed) {
      // Daha önce silent renew başarısız oldu, tekrar denemiyoruz
      throw new Error('Silent renew previously failed');
    }

    try {
      user = await this.runSilentRenew();
    } catch (err: any) {
      if (err instanceof Error && /timeout/i.test(err.message)) {
        console.warn('Silent renew timeout:', err.message);
      } else {
        console.warn('Silent renew failed:', err);
      }
      throw err;
    }

    if (!user || !user.access_token || user.expired) {
      this.silentRenewFailed = true;
      throw new Error('Silent renew did not return a valid user');
    }

    return user;
  }

  // 🔹 Silent renew Access Token Expire olduğunda app.settingste yapılan ayarlar doğrultusunda  gelen url in çağırdığı silent-callback.component tarafından tetikleniyor ve User ı refresh ederek token accessToken ı yeniliyor - REFREH TOKEN
  silentSignInAuthentication(): Promise<void> {
    
    return this.userManager.signinSilentCallback()
      .then(user => {
        this.userSubject.next(user)
        
      });
  }

  // 🔹 Logout
  async logout(): Promise<void> {
    try {
      runInInjectionContext(this.injector, () => {
        // eski hâli (06.10.2026): inject(NotificationService).destroyConnect();
        inject(RealtimeService).durdur();
      });
    } catch {}

    // Local state temizle
    this.userSubject.next(null);

    localStorage.removeItem('kullanici');
    localStorage.removeItem(
      `oidc.user:${environment.identityConfig.authority}:${environment.identityConfig.client_id}`
    );

    // IdentityServer tarafında oturumu kapat ve logout callback'ine yönlendir
    try {
      if (Capacitor.isNativePlatform()) {
        const req = await this.userManager.createSignoutRequest({
          post_logout_redirect_uri: this.NATIVE_LOGOUT_URL,
        });
        await Browser.open({ url: req.url });
        return;
      }

      await this.userManager.signoutRedirect({
        post_logout_redirect_uri: environment.identityConfig.post_logout_redirect_uri
      });
    } catch (e) {

      // Her durumda en azından ana sayfaya dön
      window.location.href = environment.identityConfig.post_logout_redirect_uri || '/';
    }
  }


  // 🔹 ROLE helpers (NULL SAFE)
  hasRole(role: string): boolean {
    return this.userSubject.value?.profile['role']?.includes(role) ?? false;
  }

  isUserAdmin(): boolean {
    return this.hasRole('Administrator');
  }

  isListEditUserAdmin(): boolean {
    return this.hasRole('DaBAdmin');
  }

  isUserPersonel(): boolean {
    return this.hasRole('Personel');
  }

  isListRole() {
    const roles = this.userSubject.value?.profile['role'] ?? [];
    return {
      isDaBAdmin: roles.includes('DaBAdmin'),
      isListAdmin: roles.includes('ListAdmin'),
      isElse: !roles.includes('DaBAdmin') && !roles.includes('ListAdmin')
    };
  }

  async completeLogout(): Promise<void> {
    try {
      // IdentityServer logout callback'ini tamamla
      await this.userManager.signoutRedirectCallback();
    } catch (e) {
      console.warn('signoutRedirectCallback error:', e);
    }

    this.userSubject.next(null);
    localStorage.removeItem('kullanici');
    localStorage.removeItem(
      `oidc.user:${environment.identityConfig.authority}:${environment.identityConfig.client_id}`
    );

    // Uygulamayı temiz bir şekilde root'a yönlendir
    window.location.href = '/';
  }

  waitForUser(): Promise<User | null> {
    return this.user$.pipe(
      filter(u => u !== null), // user yüklendi
      take(1)
    ).toPromise();
  }

   async startLogout(): Promise<void> {
  try {

  runInInjectionContext(this.injector, () => {
    // eski hâli (06.10.2026):
    // const notificationService = inject(NotificationService);
    // notificationService.destroyConnect()
    inject(RealtimeService).durdur();
  });
  }catch {
  }
    localStorage.removeItem('kullanici');

    if (Capacitor.isNativePlatform()) {
      const req = await this.userManager.createSignoutRequest({
        post_logout_redirect_uri: this.NATIVE_LOGOUT_URL,
      });
      await Browser.open({ url: req.url });
      return;
    }

  //  return await this.userManager.signoutPopupCallback();
    return await this.userManager.signoutRedirect();

  }

}



// import {  Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
// import { BehaviorSubject, Observable, catchError, from, map, of, tap, throwError } from 'rxjs';
// import * as Oidc from 'oidc-client';
// import {  User, UserManager } from 'oidc-client';
// import { AppSettingsService, UtilsService, environment } from '@dabnet-kamu/libs/ui';
// import { NotificationService } from 'libs/ui/src/lib/shared/services/notification.service';
//
// @Injectable(
//   {
//   providedIn: 'root'
// }
// )
// export class AuthService {
//
//   isUserDefined = false;
//   private user: User;
//    public userManager: UserManager=new Oidc.UserManager(environment?.identityConfig)
//   private userSubject = new BehaviorSubject<User | null>(null);
//   private utilService: UtilsService=inject(UtilsService);
//
//   private injector: Injector = inject(Injector);
//
//   constructor() {
//      const appSettingsService=inject(AppSettingsService);
//
//     appSettingsService.settings$.subscribe({
//       next: (settings) => {
//         if (settings) {
//           this.initializeUser();
//         }
//       },
//       error: (err) => this.utilService.showError(err)
//     });
//
//   }
//
//
//     initializeUser(): void {
//
//      this.userManager= new Oidc.UserManager(environment?.identityConfig);
//
//     this.userManager.getUser().then((user) => {
//       this.user = user;
//       this.isUserDefined = true;
//       this.userSubject.next(user);
//
//     }).catch((error) => {
//       this.utilService.showError(error.toString());
//
//     });
//
//   }
//
//   get user$(): Observable<User | null> {
//     return this.userSubject.asObservable();
//   }
//
//   get loggedUser(): User | null {
//     return this.userSubject?.value;
//   }
//
//
//   getUserInfo(): Observable<User | null> {
//
//     if(!this.userManager){
//
//         this.userManager= new Oidc.UserManager(environment.identityConfig);
//     }
//     return from(this.userManager?.getUser()).pipe(
//       map(user => {
//         this.user = user;
//         this.userSubject.next(user);
//         return user;
//       }),
//       catchError(error => {
//         this.utilService.showError(error);
//         return of(null);
//       })
//     );
//
//   }
//
//
//
//   getAccessToken(): Observable<string> {
//     if(!this.userManager){
//
//       this.userManager= new Oidc.UserManager(environment.identityConfig);
//   }
//     return this.user$.pipe(
//       map(user =>user?.access_token ||'' ),
//       catchError(error => {
//         this.utilService.showError(error.toString());
//
//         return of('');
//       })
//     );
//   }
//
//
//   isLoggedIn() {
//     return (this.user != null);
//
//   }
//
//   isLoggedIn$(): Observable<boolean> {
//     return this.user$.pipe(
//       map(user => user != null && !user.expired)
//     );
//   }
//
//   startAuthentication(): Promise<void> {
//     return this.userManager.signinRedirect();
//   }
//
//
//   completeAuthentication(): Observable<User> {
//     return from(this.userManager.signinRedirectCallback()).pipe(
//       tap(user => {
//         this.user = user;
//         this.isUserDefined = true;
//         this.userSubject.next(user);
//       }),
//       catchError(err => {
//         console.error(err.toString())
//         return throwError(err);
//       })
//     );
//   }
//
//  async startLogout(): Promise<void> {
//   try {
//
//   runInInjectionContext(this.injector, () => {
//     const notificationService = inject(NotificationService);
//     notificationService.destroyConnect()
//   });
//   }catch {
//   }
//     localStorage.removeItem('kullanici');
//
//   //  return await this.userManager.signoutPopupCallback();
//     return await this.userManager.signoutRedirect();
//
//   }
//
//   directLogout(): Promise<void> {
//     return this.userManager.signoutRedirectCallback().then(() => {
//       this.userManager.removeUser();
//
//       window.location.href = '/';
//     });
//   }
//
//
//   completeLogout() {
//     this.user = null;
//     this.userSubject.next(this.user);
//     return this.userManager.signoutRedirectCallback().then(() => {
//       window.location.href = '/';
//       this.userManager.removeUser();
//     });
//   }
//
//
//   silentSignInAuthentication() {
//     return this.userManager.signinSilentCallback().then((res)=>{
//       this.user=res;
//       this.userSubject.next(res);
//     });
//     }
//
//   isUserAdmin() {
//     let isAdmin = false
//     const roles = this.loggedUser.profile['role'];
//     if (roles && Array.isArray(roles)) {
//        isAdmin = roles.includes('Administrator');
//     }
//     return isAdmin
//   }
//
//   isListEditUserAdmin() {
//     let isListAdmin = false
//     const roles = this.loggedUser.profile['role'];
//     if (roles && Array.isArray(roles)) {
//       isListAdmin = roles.includes('DaBAdmin');
//     }
//     return isListAdmin
//   }
//   isListRole(): { isDaBAdmin: boolean; isListAdmin: boolean; isElse: boolean } {
//     const roles = this.loggedUser.profile['role'] || [];
//
//     const isDaBAdmin = roles.includes('DaBAdmin');
//     const isListAdmin = roles.includes('ListAdmin');
//     const isElse = !isDaBAdmin && !isListAdmin;
//
//     return { isDaBAdmin, isListAdmin, isElse };
//   }
//
//
//   isUserPersonel():boolean {
//     let isPersonel = false
//     const roles = this.loggedUser.profile['role'];
//     if (roles) {
//       isPersonel = roles.includes('Personel');
//
//     }
//     return isPersonel
//   }
//   async logout(): Promise<void> {
//   try {
//     console.log('🔹 Logout başlatılıyor...');
//
//     this.user = null;
//     this.userSubject.next(null);
//     this.isUserDefined = false;
//
//     localStorage.removeItem('kullanici');
//     localStorage.removeItem(`oidc.user:${environment.identityConfig.authority}:${environment.identityConfig.client_id}`);
//
//     await this.userManager.signoutRedirect({
//       post_logout_redirect_uri: environment.identityConfig.post_logout_redirect_uri
//     });
//
//     setTimeout(() => {
//       console.warn('⚠️ signoutRedirect sonrası yönlendirme görünmüyor, fallback devrede...');
//       window.location.href = environment.identityConfig.post_logout_redirect_uri;
//     }, 1500);
//
//   } catch (error) {
//     console.error('🚨 Logout hatası:', error);
//
//     window.location.href = environment.identityConfig.post_logout_redirect_uri;
//   }
// }
// }
